import { useState, useEffect } from "react";
import { checkpoints } from "../constants";
import { useColors } from "../ColorsContext";
import ProgressDots from "../components/ProgressDots";
import WristbandCTA from "../components/WristbandCTA";

const MAX_XP = checkpoints.reduce((sum, cp) => sum + cp.xp, 0);
const MAX_TRIES = 3;

const GEAR_ORDER = [
  { emoji: "🛡️", name: "Iron Shield" },
  { emoji: "📜", name: "Ancient Scroll" },
  { emoji: "🧥", name: "Stealth Cloak" },
  { emoji: "🦅", name: "Wings of Ascension" },
  { emoji: "⚡", name: "Power Surge" },
  { emoji: "🗡️", name: "Dragon Sword" },
];

export default function ScreenCheckpointGamer({
  path, checkpoint, checkpointIndex, totalCheckpoints, xpTotal, gearUnlocked, onComplete, onBack, layout,
}) {
  const C = useColors();
  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const edgeStyle = {
    marginLeft: -extraLeft, width: vpWidth / scale,
    paddingLeft: extraLeft + 14, paddingRight: extraLeft + 14,
  };
  const [selected, setSelected] = useState(null);
  const [correct, setCorrect] = useState(false);
  const [tries, setTries] = useState(MAX_TRIES);
  const [wrongMsg, setWrongMsg] = useState("");
  const [shake, setShake] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState("physical");
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const outOfTries = tries === 0 && !correct;
  const canScan = correct || outOfTries;

  function computeXP() {
    if (outOfTries) return 0;
    const triesUsed = MAX_TRIES - tries;
    return Math.max(0, Math.round(checkpoint.xp * (1 - 0.2 * triesUsed)));
  }

  function handleAnswer(idx) {
    if (correct || outOfTries) return;
    setSelected(idx);
    const isCorrect = idx === checkpoint.gamerChallenge.correct;
    if (isCorrect) {
      setCorrect(true);
      setWrongMsg("");
      setTimeout(() => setShowUnlock(true), 500);
    } else {
      const newTries = tries - 1;
      setTries(newTries);
      setShake(true);
      setWrongMsg(newTries === 0 ? "" : `✗ Incorrect — ${newTries} attempt${newTries !== 1 ? "s" : ""} left`);
      setTimeout(() => setSelected(null), 600);
    }
  }

  useEffect(() => {
    if (!shake) return;
    const t = setTimeout(() => setShake(false), 400);
    return () => clearTimeout(t);
  }, [shake]);

  function handleScan() {
    if (!canScan) return;
    setScanning(true);
    setTimeout(() => onComplete({ xp: computeXP(), gear: correct ? checkpoint.gear.name : null }), 1200);
  }

  const displayXP = computeXP();
  const xpAfter = xpTotal + displayXP;
  const xpPercent = Math.min(100, Math.round((xpAfter / MAX_XP) * 100));
  const currentXpPercent = Math.min(100, Math.round((xpTotal / MAX_XP) * 100));

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy, position: "relative" }}>

      {/* Top bar */}
      <div style={{
        background: C.navyMid, paddingTop: 10, paddingBottom: 10,
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        <button
          onClick={() => setShowLeaveModal(true)}
          style={{
            background: "transparent", border: "none",
            color: C.muted, fontSize: 20, cursor: "pointer",
            padding: "4px 6px", lineHeight: 1, flexShrink: 0,
          }}
        >←</button>
        <div style={{ fontSize: 11, color: path.color, fontWeight: 700, letterSpacing: 1, flex: 1 }}>
          {path.label.toUpperCase()} · CP {checkpointIndex + 1}/{totalCheckpoints}
        </div>
        <ProgressDots total={totalCheckpoints} current={checkpointIndex} />
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
        {/* Participation toggle */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 12,
          background: C.card, borderRadius: 8, padding: 4,
          border: `1px solid ${C.dim}`,
        }}>
          {[["physical", "🏃 Physical"], ["alternative", "♿ Alternative"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: "8px 0", borderRadius: 6, border: "none",
              background: mode === m ? path.color : "transparent",
              color: mode === m ? "#fff" : C.muted,
              fontWeight: 600, fontSize: 12, cursor: "pointer",
            }}>{label}</button>
          ))}
        </div>

        {/* Hero panel */}
        <div style={{
          background: `${C.purple}15`, borderRadius: 10, padding: 12,
          border: `1px solid ${C.purple}44`, marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: C.purple, letterSpacing: 1 }}>HERO · DRAGON'S KEEP</div>
            <div style={{ fontSize: 11, color: C.gold, fontWeight: 700 }}>{xpTotal} / {MAX_XP} XP</div>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {GEAR_ORDER.map((g, i) => {
              const isUnlocked = gearUnlocked.includes(g.name);
              const isCurrent = i === checkpointIndex && correct;
              return (
                <div key={g.name} title={g.name} style={{
                  width: 34, height: 34, borderRadius: 8,
                  background: isUnlocked || isCurrent ? `${C.purple}33` : C.card,
                  border: `2px solid ${isUnlocked || isCurrent ? C.purple : C.dim}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: isUnlocked || isCurrent ? 17 : 13,
                  opacity: isUnlocked || isCurrent ? 1 : 0.3,
                  transition: "all 0.4s",
                  transform: isCurrent && showUnlock ? "scale(1.2)" : "scale(1)",
                }}>
                  {isUnlocked || isCurrent ? g.emoji : "🔒"}
                </div>
              );
            })}
          </div>
          <div style={{ background: C.dim, borderRadius: 4, height: 8, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 4,
              background: `linear-gradient(90deg, ${C.purple}, ${C.pink})`,
              width: `${correct ? xpPercent : currentXpPercent}%`,
              transition: "width 0.8s ease",
            }} />
          </div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4, textAlign: "right" }}>
            {correct ? xpPercent : currentXpPercent}% powered up
          </div>
        </div>

        {/* Gear unlock animation */}
        {showUnlock && (
          <div style={{
            background: `${C.purple}25`, borderRadius: 10, padding: 14,
            border: `2px solid ${C.purple}`, marginBottom: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>{checkpoint.gear.emoji}</div>
            <div style={{ fontWeight: 800, color: C.purple, fontSize: 16, marginBottom: 4 }}>
              🔊 {checkpoint.gear.name} Unlocked!
            </div>
            <div style={{ fontSize: 12, color: C.light, lineHeight: 1.5 }}>{checkpoint.gear.desc}</div>
          </div>
        )}

        {/* Story context — compact */}
        <div style={{
          fontSize: 12, color: C.muted, fontStyle: "italic",
          marginBottom: 12, padding: "8px 12px",
          background: C.card, borderRadius: 8,
          borderLeft: `3px solid ${path.color}`, lineHeight: 1.5,
        }}>
          {checkpoint.story[path.id]}
        </div>

        {mode === "alternative" && (
          <div style={{
            background: `${C.gold}15`, borderRadius: 8, padding: 10,
            border: `1px solid ${C.gold}44`, marginBottom: 12,
            fontSize: 12, color: C.gold, lineHeight: 1.5,
          }}>
            <strong>Alternative Role:</strong> While your teammate completes the physical obstacle,
            you answer the challenge below. Both must complete for the team to advance.
            🔊 Audio description available on request.
          </div>
        )}

        {/* Challenge */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
            CHAMBER CHALLENGE · +{displayXP} XP
          </div>
          <div style={{
            background: C.card, borderRadius: 10, padding: 14,
            border: `1px solid ${C.dim}`, marginBottom: 10,
            fontSize: 14, color: C.light, lineHeight: 1.6,
          }}>
            {checkpoint.gamerChallenge.question}
          </div>

          {outOfTries && (
            <div style={{
              background: `${C.red}18`, borderRadius: 10, padding: 12,
              border: `2px solid ${C.red}55`, marginBottom: 10, textAlign: "center",
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>⚠️</div>
              <div style={{ fontWeight: 800, color: C.red, fontSize: 14, marginBottom: 4 }}>🔊 CHALLENGE FAILED</div>
              <div style={{ fontSize: 12, color: C.light }}>0 XP earned. Scan to continue.</div>
            </div>
          )}

          {!correct && !outOfTries && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>ATTEMPTS</span>
              {[0, 1, 2].map(i => {
                const used = i >= tries;
                return (
                  <div key={i} style={{
                    width: 14, height: 14, borderRadius: "50%",
                    background: used ? "transparent" : path.color,
                    border: `2px solid ${used ? C.red : path.color}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, color: C.red, transition: "all 0.3s",
                  }}>{used ? "✕" : ""}</div>
                );
              })}
              <span style={{ fontSize: 10, color: C.muted }}>{tries} remaining</span>
            </div>
          )}

          {/* Answer options */}
          <div style={{ overflow: "auto", maxHeight: 220 }}>
          <div style={{
            display: "flex", flexDirection: "column", gap: 8,
            transform: shake ? "translateX(-6px)" : "translateX(0)",
            transition: shake ? "transform 0.1s" : "transform 0.15s",
          }}>
            {checkpoint.gamerChallenge.options.map((opt, i) => {
              const isSelected = selected === i;
              const isCorrectOption = i === checkpoint.gamerChallenge.correct;
              let bgColor = C.card, borderColor = C.dim, textColor = C.light;
              if (correct && isCorrectOption) { bgColor = `${C.teal}22`; borderColor = C.teal; textColor = C.teal; }
              else if (outOfTries && isCorrectOption) { bgColor = `${C.teal}12`; borderColor = `${C.teal}66`; }
              return (
                <button
                  key={i}
                  onClick={() => !correct && !outOfTries && handleAnswer(i)}
                  disabled={correct || outOfTries}
                  style={{
                    padding: "13px 14px", background: bgColor,
                    border: `2px solid ${borderColor}`, borderRadius: 8,
                    color: textColor, fontSize: 13,
                    cursor: correct || outOfTries ? "default" : "pointer",
                    textAlign: "left", fontWeight: isSelected ? 700 : 500,
                    transition: "all 0.2s", minHeight: 48,
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <span style={{
                    width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                    border: `1px solid ${borderColor}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11,
                  }}>
                    {correct && isCorrectOption ? "✓" : String.fromCharCode(65 + i)}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          </div>

          {wrongMsg && (
            <div role="alert" style={{ fontSize: 12, color: C.red, marginTop: 8, paddingLeft: 4, fontWeight: 600 }}>
              {wrongMsg}
            </div>
          )}
        </div>
      </div>

      {/* Sticky wristband CTA */}
      <WristbandCTA
        canScan={canScan}
        outOfTries={outOfTries}
        pathColor={path.color}
        scanning={scanning}
        onScan={handleScan}
        layout={layout}
      />

      {/* Leave modal */}
      {showLeaveModal && (
        <div style={{
          position: "absolute", inset: 0, background: "rgba(0,0,0,0.72)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100, padding: 24,
        }}>
          <div style={{
            background: C.card, borderRadius: 14, padding: 24,
            border: `1px solid ${C.dim}`, width: "100%",
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.white, marginBottom: 8 }}>
              Leave this checkpoint?
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Your progress on this challenge will be lost.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLeaveModal(false)}
                style={{
                  flex: 1, padding: "12px 0", background: "transparent",
                  border: `1px solid ${C.dim}`, borderRadius: 8,
                  color: C.muted, fontSize: 13, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={() => { setShowLeaveModal(false); onBack(); }}
                style={{
                  flex: 1, padding: "12px 0", background: C.red,
                  border: "none", borderRadius: 8,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
